/**
 * @fileoverview Case Ingestion Types - Type definitions and Zod schemas for audit records.
 *
 * The audit record tracks which sections have been reviewed during ingestion.
 * Each section can be: null (not reviewed), 'found' (data entered), or 'not_found'.
 *
 * Sections tracked:
 * - first_party_coverage, third_party_coverage, health_insurance (Coverage step)
 * - police_report (Police step)
 * - medical_providers (Treatment step)
 * - retainer_document, hipaa_document (Documents step)
 * - companion_cases (Companion Cases step)
 */
import { z } from 'zod';

// =============================================================================
// SECTION IDS
// =============================================================================

export const INGESTION_SECTIONS = [
  'first_party_coverage',
  'third_party_coverage',
  'health_insurance',
  'police_report',
  'medical_providers',
  'retainer_document',
  'hipaa_document',
  'companion_cases',
] as const;

export type IngestionSection = (typeof INGESTION_SECTIONS)[number];

// =============================================================================
// STATUS VALUES
// =============================================================================

export const INGESTION_SECTION_STATUS = ['found', 'not_found'] as const;
export type IngestionSectionStatus = (typeof INGESTION_SECTION_STATUS)[number];

// =============================================================================
// AUDIT RECORD SCHEMAS (strict Zod validation)
// =============================================================================

/** Section status: null (not reviewed), "found", or "not_found" */
const SectionStatusSchema = z.enum(['found', 'not_found']).nullable();

/** All sections in the audit record — each is null or a status value */
export const IngestionSectionsSchema = z.object({
  first_party_coverage: SectionStatusSchema,
  third_party_coverage: SectionStatusSchema,
  health_insurance: SectionStatusSchema,
  police_report: SectionStatusSchema,
  medical_providers: SectionStatusSchema,
  retainer_document: SectionStatusSchema,
  hipaa_document: SectionStatusSchema,
  companion_cases: SectionStatusSchema,
});
export type IngestionSections = z.infer<typeof IngestionSectionsSchema>;

/** Full audit record from the API */
export const CaseIngestionResultSchema = z.object({
  id: z.string(),
  matter_id: z.string(),
  completed_by: z.string().nullable(),
  completed_at: z.string().nullable(),
  sections: IngestionSectionsSchema,
  created_at: z.string(),
  modified_at: z.string(),
});
export type CaseIngestionResult = z.infer<typeof CaseIngestionResultSchema>;

// =============================================================================
// REQUEST TYPES
// =============================================================================

/** Request body for creating an audit record */
export interface CreateCaseIngestionRequest {
  matter_id: string;
}

/** Request body for updating one or more section statuses (batch) */
export interface UpdateCaseIngestionSectionsRequest {
  sections: Partial<Record<IngestionSection, IngestionSectionStatus | null>>;
}

/** Request body for completing the ingestion */
export interface CompleteCaseIngestionRequest {
  completed_by?: string;
}

// =============================================================================
// DERIVE HELPERS
// =============================================================================

/**
 * Create empty sections (all null = not reviewed) for a fresh audit record.
 */
export function createEmptySections(): IngestionSections {
  return {
    first_party_coverage: null,
    third_party_coverage: null,
    health_insurance: null,
    police_report: null,
    medical_providers: null,
    retainer_document: null,
    hipaa_document: null,
    companion_cases: null,
  };
}

/**
 * Check if a section has been reviewed (status is not null).
 */
export function isSectionReviewed(
  sections: IngestionSections,
  section: IngestionSection,
): boolean {
  return sections[section] !== null;
}

/**
 * Check if all sections have been reviewed.
 */
export function areAllSectionsReviewed(sections: IngestionSections): boolean {
  return INGESTION_SECTIONS.every((section) => sections[section] !== null);
}

/**
 * Get the count of reviewed sections.
 */
export function getReviewedSectionCount(sections: IngestionSections): number {
  return INGESTION_SECTIONS.filter((section) => sections[section] !== null).length;
}
